import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../Contexts/AuthContext";

export default function Login() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [data, setData] = useState({ username: "", password: "" });
  const [focused, setFocused] = useState({ username: false, password: false });
  const [error, setError] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async () => {
    if (!data.username || !data.password) {
      setError("Please fill in all fields.");
      return;
    }

    setIsLoggingIn(true);
    setError("");
    
    const result = await login(data.username, data.password);
    
    if (result.success) {
      navigate("/dashboard");
    } else {
      setError(result.message);
    }
    setIsLoggingIn(false);
  };

  const floatLabel = (field) =>
    focused[field] || data[field]?.length > 0;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .login-root {
          display: flex;
          min-height: 100vh;
          font-family: 'DM Sans', sans-serif;
          background: #0d0d0d;
        }

        /* ── LEFT PANEL ── */
        .login-left {
          flex: 1.1;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 60px;
          overflow: hidden;
          background: linear-gradient(135deg, #1a2a00 0%, #2d4a00 40%, #5a7a00 80%, #c8b400 100%);
        }

        .login-left::before {
          content: '';
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse 60% 50% at 20% 80%, rgba(121,159,12,0.45) 0%, transparent 70%),
            radial-gradient(ellipse 50% 60% at 80% 20%, rgba(255,224,0,0.25) 0%, transparent 65%);
          pointer-events: none;
        }

        .noise-overlay {
          position: absolute;
          inset: 0;
          opacity: 0.06;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
          background-size: 200px 200px;
          pointer-events: none;
        }

        .left-content {
          position: relative;
          z-index: 1;
          max-width: 440px;
          animation: fadeSlideLeft 0.4s cubic-bezier(.22,1,.36,1) both;
        }

        .brand-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 100px;
          padding: 6px 16px 6px 8px;
          margin-bottom: 36px;
          backdrop-filter: blur(8px);
        }

        .brand-dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          background: #c8e000;
          box-shadow: 0 0 8px #c8e000;
          animation: pulse 2s ease-in-out infinite;
        }

        .brand-badge span {
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          font-weight: 500;
          color: rgba(255,255,255,0.75);
          letter-spacing: 0.05em;
          text-transform: uppercase;
        }

        .logo-text {
          font-family: 'Syne', sans-serif;
          font-size: clamp(38px, 5vw, 58px);
          font-weight: 800;
          line-height: 1.0;
          color: #fff;
          letter-spacing: -0.02em;
          margin-bottom: 24px;
        }

        .logo-text span {
          color: #d4e800;
        }

        .quote-text {
          font-size: clamp(14px, 1.5vw, 16px);
          line-height: 1.75;
          color: rgba(255,255,255,0.6);
          font-weight: 300;
          max-width: 360px;
        }

        .stat-row {
          display: flex;
          gap: 32px;
          margin-top: 52px;
          padding-top: 32px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }

        .stat-item { display: flex; flex-direction: column; gap: 4px; }

        .stat-num {
          font-family: 'Syne', sans-serif;
          font-size: 26px;
          font-weight: 700;
          color: #d4e800;
        }

        .stat-label {
          font-size: 11px;
          color: rgba(255,255,255,0.45);
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        /* ── RIGHT PANEL ── */
        .login-right {
          flex: 0.9;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px 32px;
          background: #0d0d0d;
        }

        .login-card {
          width: 100%;
          max-width: 400px;
          animation: fadeSlideRight 0.4s 0.1s cubic-bezier(.22,1,.36,1) both;
        }

        .card-eyebrow {
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #799f0c;
          margin-bottom: 12px;
        }

        .card-title {
          font-family: 'Syne', sans-serif;
          font-size: clamp(28px, 3vw, 36px);
          font-weight: 700;
          color: #f0f0f0;
          margin-bottom: 8px;
          letter-spacing: -0.02em;
        }

        .card-subtitle {
          font-size: 14px;
          color: #555;
          margin-bottom: 40px;
        }

        /* Floating label inputs */
        .field {
          position: relative;
          margin-bottom: 20px;
        }

        .field input {
          width: 100%;
          padding: 20px 16px 8px;
          background: #161616;
          border: 1px solid #2a2a2a;
          border-radius: 10px;
          font-size: 15px;
          font-family: 'DM Sans', sans-serif;
          color: #f0f0f0;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
          -webkit-appearance: none;
        }

        .field input:focus {
          border-color: #799f0c;
          box-shadow: 0 0 0 3px rgba(121,159,12,0.12);
        }

        .field input:-webkit-autofill {
          -webkit-box-shadow: 0 0 0 100px #161616 inset;
          -webkit-text-fill-color: #f0f0f0;
        }

        .field label {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 14px;
          color: #555;
          pointer-events: none;
          transition: all 0.2s cubic-bezier(.22,1,.36,1);
          font-family: 'DM Sans', sans-serif;
        }

        .field label.floating {
          top: 10px;
          transform: none;
          font-size: 11px;
          color: #799f0c;
          letter-spacing: 0.04em;
          font-weight: 500;
        }

        .login-btn {
          width: 100%;
          padding: 15px;
          margin-top: 8px;
          background: linear-gradient(135deg, #799f0c, #9fcc10);
          color: #0d0d0d;
          border: none;
          border-radius: 10px;
          font-size: 15px;
          font-family: 'Syne', sans-serif;
          font-weight: 700;
          letter-spacing: 0.03em;
          cursor: pointer;
          transition: transform 0.15s, box-shadow 0.15s, filter 0.15s;
          position: relative;
          overflow: hidden;
        }

        .login-btn::after {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(255,255,255,0);
          transition: background 0.2s;
        }

        .login-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 8px 24px rgba(121,159,12,0.35);
          filter: brightness(1.08);
        }

        .login-btn:active { transform: translateY(0); box-shadow: none; }

        .divider {
          display: flex;
          align-items: center;
          gap: 12px;
          margin: 24px 0;
        }

        .divider-line { flex: 1; height: 1px; background: #222; }
        .divider-text { font-size: 11px; color: #3a3a3a; letter-spacing: 0.06em; text-transform: uppercase; }

        .demo-pill {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px 16px;
          background: #141414;
          border: 1px dashed #2e2e2e;
          border-radius: 8px;
          font-size: 12px;
          color: #444;
          font-family: 'DM Mono', 'Courier New', monospace;
        }

        .demo-pill strong { color: #666; }

        /* ── KEYFRAMES ── */
        @keyframes fadeSlideLeft {
          from { opacity: 0; transform: translateX(-30px); }
          to   { opacity: 1; transform: translateX(0); }
        }

        @keyframes fadeSlideRight {
          from { opacity: 0; transform: translateX(30px); }
          to   { opacity: 1; transform: translateX(0); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.6; transform: scale(1.4); }
        }

        /* ── RESPONSIVE ── */
        @media (max-width: 768px) {
          .login-root { flex-direction: column; }

          .login-left {
            flex: none;
            padding: 48px 32px 40px;
          }

          .stat-row { display: none; }

          .login-right {
            flex: 1;
            padding: 40px 24px;
          }

          .login-card { max-width: 100%; }
        }

        @media (max-width: 400px) {
          .login-left { padding: 36px 24px 32px; }
          .logo-text { font-size: 32px; }
        }
      `}</style>

      <div className="login-root">

        {/* LEFT */}
        <div className="login-left">
          <div className="noise-overlay" />
          <div className="left-content">
            <div className="brand-badge">
              <div className="brand-dot" />
              <span>Now Live</span>
            </div>

            <h1 className="logo-text">
              Done<span>In</span><br />Seconds.
            </h1>

            <p className="quote-text">
              Speed is the new currency of business.<br />
              Build fast. Ship faster. Scale without limits.
            </p>

            <div className="stat-row">
              <div className="stat-item">
                <span className="stat-num">10x</span>
                <span className="stat-label">Faster builds</span>
              </div>
              <div className="stat-item">
                <span className="stat-num">99%</span>
                <span className="stat-label">Uptime SLA</span>
              </div>
              <div className="stat-item">
                <span className="stat-num">0ms</span>
                <span className="stat-label">Cold starts</span>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="login-right">
          <div className="login-card">
            <p className="card-eyebrow">→ Secure Access</p>
            <h2 className="card-title">Welcome back</h2>
            <p className="card-subtitle">Sign in to your workspace</p>

            {error && (
              <div style={{ color: "#ff4d4d", fontSize: "13px", marginBottom: "15px", textAlign: "center" }}>
                {error}
              </div>
            )}

            {/* Username */}
            <div className="field">
              <input
                id="username"
                type="text"
                autoComplete="username"
                value={data.username}
                onChange={(e) => setData({ ...data, username: e.target.value })}
                onFocus={() => setFocused({ ...focused, username: true })}
                onBlur={() => setFocused({ ...focused, username: false })}
              />
              <label htmlFor="username" className={floatLabel("username") ? "floating" : ""}>
                Username
              </label>
            </div>

            {/* Password */}
            <div className="field">
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                value={data.password}
                onChange={(e) => setData({ ...data, password: e.target.value })}
                onFocus={() => setFocused({ ...focused, password: true })}
                onBlur={() => setFocused({ ...focused, password: false })}
              />
              <label htmlFor="password" className={floatLabel("password") ? "floating" : ""}>
                Password
              </label>
            </div>

            <button 
              className="login-btn" 
              onClick={handleLogin}
              disabled={isLoggingIn}
              style={{ opacity: isLoggingIn ? 0.7 : 1, cursor: isLoggingIn ? "not-allowed" : "pointer" }}
            >
              {isLoggingIn ? "Signing In..." : "Sign In"}
            </button>

            {/* Demo credentials removed */}
          </div>
        </div>

      </div>
    </>
  );
}